// The per-hour strip above the rows (B3): for each hour, how many therapists
// are on duty, how many are busy, how many are free, and how many bookings
// are still waiting for a therapist.
//
// COUNTS, not "capacity" (existing rule): whether a free therapist can take a
// given booking also depends on skills and travel, which is the Assign view's
// job (calendar-board.assign.ts), not this strip's.
//
// An hour is judged at its middle (5:30 for the 5 PM hour), so a session that
// ends at 5:15 does not make the whole hour look busy. The strip follows the
// hub filter only: "who" and search narrow the rows, never the hub's real
// availability.

import {
  type BoardRow,
  type CalendarDay,
  type CalendarFilters,
  inHub,
  type MinuteSpan,
  type SessionBlock,
} from "./calendar-board";

export interface HourCell {
  startMin: number;
  onDuty: number;
  busy: number;
  free: number;
  waiting: number;
  /** The whole hour is behind the clock. */
  past: boolean;
}

export interface HourPerson {
  row: BoardRow;
  /** The session they are in at the middle of the hour, or `null` when free. */
  session: SessionBlock | null;
}

export interface HourDetail {
  cell: HourCell;
  waiting: SessionBlock[];
  /** Free first, then busy — the operator is looking for someone free. */
  people: HourPerson[];
}

const covers = (span: MinuteSpan, minute: number) =>
  span.startMin <= minute && span.endMin > minute;

function onDutyTherapists(day: CalendarDay, filters: CalendarFilters, minute: number) {
  return day.rows.filter(
    (row) =>
      row.role === "therapist" &&
      inHub(filters, row.hubId) &&
      row.shift !== null &&
      covers(row.shift, minute),
  );
}

function sessionAt(row: BoardRow, minute: number): SessionBlock | null {
  const block = row.blocks.find((entry) => entry.kind === "session" && covers(entry, minute));
  return block?.kind === "session" ? block : null;
}

function waitingAt(day: CalendarDay, filters: CalendarFilters, minute: number) {
  return day.unassigned.filter(
    (session) => inHub(filters, session.hubId) && covers(session, minute),
  );
}

export function hourCell(day: CalendarDay, filters: CalendarFilters, startMin: number): HourCell {
  const mid = startMin + 30;
  const onDuty = onDutyTherapists(day, filters, mid);
  const busy = onDuty.filter((row) => sessionAt(row, mid) !== null).length;
  return {
    startMin,
    onDuty: onDuty.length,
    busy,
    free: onDuty.length - busy,
    waiting: waitingAt(day, filters, mid).length,
    past: startMin + 60 <= day.nowMin,
  };
}

export function hourStrip(
  day: CalendarDay,
  filters: CalendarFilters,
  range: MinuteSpan,
): HourCell[] {
  const cells: HourCell[] = [];
  for (let start = range.startMin; start < range.endMin; start += 60) {
    cells.push(hourCell(day, filters, start));
  }
  return cells;
}

export function hourDetail(
  day: CalendarDay,
  filters: CalendarFilters,
  startMin: number,
): HourDetail {
  const mid = startMin + 30;
  const people = onDutyTherapists(day, filters, mid)
    .map((row) => ({ row, session: sessionAt(row, mid) }))
    .sort(
      (a, b) =>
        Number(a.session !== null) - Number(b.session !== null) ||
        a.row.name.localeCompare(b.row.name),
    );
  return {
    cell: hourCell(day, filters, startMin),
    waiting: waitingAt(day, filters, mid),
    people,
  };
}
